import { clearCombinedToken, getCombinedToken } from '@local/login';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router';

import { useGetUserQuery, useSignoutMutation, useValidateQuery } from 'src/apiClients';
import { getAccessLevelLabel } from 'src/utils/get-access-level-label';
import { LEGACY_TOKEN_TYPE } from 'src/utils/strings';

import { SideBarItemButton } from '../side-bar-item-button/SideBarItemButton';
import { i18n } from './ProfileFooter.i18n';

type ProfileFooterProps = {
    expanded: boolean;
};

export const ProfileFooter = ({ expanded }: ProfileFooterProps) => {
    const intl = useIntl();
    const navigate = useNavigate();

    const { data: validateData } = useValidateQuery(undefined);
    const { data: user } = useGetUserQuery(validateData?.uid ?? '', {
        skip: !validateData?.uid,
    });
    const [signoutRequest] = useSignoutMutation();

    const displayName = user?.name || user?.login || '';
    const accessLevel = getAccessLevelLabel(user, intl);
    const tooltipText = [displayName, accessLevel].filter(Boolean).join(' · ');

    const signOut = async () => {
        const token = getCombinedToken();
        if (token?.token_type === LEGACY_TOKEN_TYPE) {
            try {
                await signoutRequest().unwrap();
            } catch {
                // Even if the server call fails (e.g. token already expired) we
                // still want to drop local state and send the user to /login.
            }
            clearCombinedToken();
            navigate('/login');
            return;
        }
        // OAuth/SSO sessions — hand off to LoginHandler's /logout route, which
        // knows how to end the IdP session and redirect back.
        navigate('/logout');
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: expanded ? 'flex-start' : 'center',
                    minHeight: 56,
                    px: expanded ? 2 : 0,
                    gap: 1.5,
                }}
                automation-id="profile-footer-user"
            >
                <Tooltip title={expanded ? '' : tooltipText} placement="right">
                    <Avatar sx={{ width: 32, height: 32 }}>
                        <PersonIcon fontSize="small" />
                    </Avatar>
                </Tooltip>
                {expanded && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                        {displayName && (
                            <Typography variant="body2" noWrap title={displayName}>
                                {displayName}
                            </Typography>
                        )}
                        {accessLevel && (
                            <Typography
                                variant="caption"
                                noWrap
                                title={accessLevel}
                                sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                            >
                                {accessLevel}
                            </Typography>
                        )}
                    </Box>
                )}
            </Box>
            <Tooltip title={expanded ? '' : intl.formatMessage(i18n.signOut)} placement="right">
                <SideBarItemButton
                    onClick={signOut}
                    icon={<LogoutIcon />}
                    label={intl.formatMessage(i18n.signOut)}
                    expanded={expanded}
                    automation-id="profile-footer-sign-out"
                />
            </Tooltip>
        </Box>
    );
};
